import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "../../utils/api";
import { formatMoney, formatDateLong, formatDateWithTime, formatDateInputValue, getNairobiMonth, getNairobiYear } from "../../utils/format";
import SearchableDropdown from "../SearchableDropdown";
import PayScoreGauge from "../PayScoreGauge";
import Icon from "../Icon";
import { SkeletonStatGrid, SkeletonList } from "../Skeleton";
import { getUtilitySettlementId, getUtilityWalletBalance, getVisibleUtilitySettlementRows } from "../../pages/utbills/utilitySettlement";

// Redirect Modal Component
const RedirectConfirmationModal = ({ show, onClose, onConfirm, property, value, loading }) => {
  if (!show || !property) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all">
        <div className="p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-orange-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-center text-gray-900 mb-2">
            Confirm Income Redirection
          </h3>
          <div className="text-center text-gray-600 mb-6 space-y-2">
            <p>
              You are about to <span className="font-bold text-gray-900">{value ? 'ENABLE' : 'DISABLE'}</span> income redirection for <span className="font-semibold text-green-600">{property.name}</span>.
            </p>
            {value ? (
              <p className="text-sm bg-orange-50 p-3 rounded-lg border border-orange-100 text-orange-800 whitespace-pre-line">
                Payment for this property will be redirected to the Property management.{'\n\n'}You will Settle externally (Outside RecordLOGS)
              </p>
            ) : (
              <p className="text-sm bg-green-50 p-3 rounded-lg border border-green-100 text-green-800">
                You will resume receiving your calculated share of the income (after management fees).
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-medium shadow-sm ${value
                ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-green-600 hover:bg-green-700'
                }`}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Saving...</span>
                </div>
              ) : (
                'Confirm'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function LandlordView({
  user,
  activeRole,
  payScore,
  payScoreLoading,
  statementLoading,
  setStatementLoading,
  setTransactionStatement,
  setSelectedUnit,
  setShowTransactionStatement,
  setShowUnitDetailsModal
}) {
  const [myProperties, setMyProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showUnitsView, setShowUnitsView] = useState(false);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unitBalances, setUnitBalances] = useState({});
  const [unitUtilityBalances, setUnitUtilityBalances] = useState({});
  const [unitBalancesLoading, setUnitBalancesLoading] = useState(false);

  // Helper: current month range in YYYY-MM-DD (Africa/Nairobi time)
  const getCurrentMonthRange = () => {
    const y = getNairobiYear();
    const m = getNairobiMonth();
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    return { start: formatDateInputValue(start), end: formatDateInputValue(end) };
  };

  // Property statements (payments) states
  const [showPropertyStatementsModal, setShowPropertyStatementsModal] = useState(false);
  const [propertyPayments, setPropertyPayments] = useState([]);
  const [propertyPaymentsLoading, setPropertyPaymentsLoading] = useState(false);
  const [propertyStatementStartDate, setPropertyStatementStartDate] = useState("");
  const [propertyStatementEndDate, setPropertyStatementEndDate] = useState("");
  const [propertyFilterOpen, setPropertyFilterOpen] = useState(false);
  const [utilityWallets, setUtilityWallets] = useState([]);
  const [utilityWalletsLoading, setUtilityWalletsLoading] = useState(false);
  const [showUtilityStatementModal, setShowUtilityStatementModal] = useState(false);
  const [selectedUtilityWallet, setSelectedUtilityWallet] = useState(null);
  const [utilityTransactions, setUtilityTransactions] = useState([]);
  const [utilityTransactionsLoading, setUtilityTransactionsLoading] = useState(false);

  // Income Redirection State
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [redirectProperty, setRedirectProperty] = useState(null);
  const [redirectActionValue, setRedirectActionValue] = useState(false);
  const [redirectLoading, setRedirectLoading] = useState(false);

  // Manager Change State
  const [showManagerChangeModal, setShowManagerChangeModal] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [availableCompaniesLoading, setAvailableCompaniesLoading] = useState(false);
  const [selectedNewCompanyAdminId, setSelectedNewCompanyAdminId] = useState('');
  const [managerChangeMessage, setManagerChangeMessage] = useState('');
  const [managerChangeLoading, setManagerChangeLoading] = useState(false);
  const [pendingManagerRequests, setPendingManagerRequests] = useState({});

  // Global company switch state
  const [bulkManagerSelection, setBulkManagerSelection] = useState([]);
  const [bulkManagerCompanyId, setBulkManagerCompanyId] = useState('');
  const [bulkManagerMessage, setBulkManagerMessage] = useState('Hi.\nI would like you to manage my properties.');
  const [bulkManagerSearch, setBulkManagerSearch] = useState('');
  const [bulkManagerLoading, setBulkManagerLoading] = useState(false);
  const [showBulkManagerPanel, setShowBulkManagerPanel] = useState(false);

  // Global settlement toggle state
  const [bulkSettlementSelection, setBulkSettlementSelection] = useState([]);
  const [bulkSettlementSearch, setBulkSettlementSearch] = useState('');
  const [bulkSettlementLoading, setBulkSettlementLoading] = useState(false);
  const [showBulkSettlementPanel, setShowBulkSettlementPanel] = useState(false);
  const [showSettlementConfirm, setShowSettlementConfirm] = useState(false);
  const [settlementConfirmAction, setSettlementConfirmAction] = useState('on');
  const [settlementConfirmCompanies, setSettlementConfirmCompanies] = useState([]);

  const sortUnitsByLabel = useCallback((list = []) => {
    return [...list].sort((a, b) =>
      String(a?.label || '').localeCompare(String(b?.label || ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
  }, []);

  // Pagination and search for redirect sections
  const [redirectOffSearch, setRedirectOffSearch] = useState('');
  const [redirectOnSearch, setRedirectOnSearch] = useState('');
  const [redirectOffShowCount, setRedirectOffShowCount] = useState(5);
  const [redirectOnShowCount, setRedirectOnShowCount] = useState(5);
  const bulkSettlementPanelRef = useRef(null);

  // Fetch properties where user is landlord
  const fetchMyProperties = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      // Load local properties with lock status so we can reflect locks even if
      // the external FRP units endpoint doesn't include that flag.
      // eslint-disable-next-line no-unused-vars
      const response = await api.get("/frp/properties");
      const props = Array.isArray(response.data?.properties) ? response.data.properties : [];
      const lockedMap = {};
      const propertyMap = {};
      props.forEach(p => {
        const appId = p?._id || p?.id;
        const frpId = p?.frpId || p?.frp_id || p?.sourceId;
        const locked = !!(p.locked || p.managementCompany?.locked);
        if (appId) lockedMap[String(appId)] = locked;
        if (frpId) lockedMap[String(frpId)] = locked;
        if (appId) propertyMap[String(appId)] = p;
        if (frpId) propertyMap[String(frpId)] = p;
      });

      // Group units by property for landlord
      // Request only units where the user is landlord
      const unitsResponse = await api.get("/frp/my-units?scope=landlord");
      const myUnits = unitsResponse.data.units;

      // Create a map of properties with their units
      const propertyUnitsMap = {};
      myUnits.forEach(unit => {
        const uProp = unit.property || {};
        const propertyId =
          uProp._id ||
          uProp.id ||
          uProp.frpId ||
          uProp.frp_id ||
          uProp.sourceId ||
          uProp;
        const key = String(propertyId);
        const baseProperty = propertyMap[String(propertyId)]
          ? { ...uProp, ...propertyMap[String(propertyId)] }
          : uProp;
        // IMPORTANT: use freshly computed lockedMap (not stale state) so that
        // locks applied in admin/manager are reflected immediately.
        const locked =
          lockedMap[key] ??
          !!(baseProperty.locked || baseProperty.managementCompany?.locked);

        if (!propertyUnitsMap[propertyId]) {
          propertyUnitsMap[propertyId] = {
            property: {
              ...baseProperty,
              _id: propertyId,
              locked
            },
            units: []
          };
        }
        propertyUnitsMap[propertyId].units.push(unit);
      });

      Object.values(propertyUnitsMap).forEach((entry) => {
        entry.units = sortUnitsByLabel(entry.units);
      });

      setMyProperties(Object.values(propertyUnitsMap));
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  }, [user, sortUnitsByLabel]);

  useEffect(() => {
    fetchMyProperties();
  }, [fetchMyProperties]);

  const fetchUtilityWallets = useCallback(async () => {
    if (!user) return;
    try {
      setUtilityWalletsLoading(true);
      const response = await api.get('/utilities/landlord/wallets');
      setUtilityWallets(Array.isArray(response.data?.wallets) ? response.data.wallets : []);
    } catch (error) {
      console.error('Failed to fetch landlord utility wallets:', error);
      setUtilityWallets([]);
    } finally {
      setUtilityWalletsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUtilityWallets();
  }, [fetchUtilityWallets]);

  const utilityWalletBalanceTotal = useMemo(
    () => utilityWallets.reduce((sum, wallet) => sum + getUtilityWalletBalance(wallet), 0),
    [utilityWallets]
  );

  const openUtilityStatement = async (wallet) => {
    const propertyId = wallet?.property?._id || wallet?.property;
    if (!propertyId) return;
    setSelectedUtilityWallet(wallet);
    setUtilityTransactions([]);
    setShowUtilityStatementModal(true);
    setUtilityTransactionsLoading(true);
    try {
      const response = await api.get(`/utilities/landlord/properties/${propertyId}/transactions`);
      setUtilityTransactions(getVisibleUtilitySettlementRows(response.data?.transactions));
      if (response.data?.wallet) {
        setSelectedUtilityWallet(response.data.wallet);
      }
    } catch (error) {
      console.error('Failed to fetch utility statement:', error);
      setUtilityTransactions([]);
    } finally {
      setUtilityTransactionsLoading(false);
    }
  };

  const normalizeId = (value) => {
    if (!value) return '';
    return typeof value.toString === 'function' ? value.toString() : String(value);
  };

  const getPropertyId = (property) =>
    property?._id || property?.id || property?.frpId || property?.frp_id || property?.sourceId || property;

  const fetchPendingManagerRequests = useCallback(async () => {
    if (!user) {
      setPendingManagerRequests({});
      return;
    }

    try {
      const res = await api.get('/frp/landlord-manager-change-requests?status=pending');
      const list = Array.isArray(res.data?.requests) ? res.data.requests : [];
      const pendingMap = {};
      list.forEach((request) => {
        const rawPropertyId = request?.property?._id || request?.property;
        const propertyId = rawPropertyId && typeof rawPropertyId.toString === 'function'
          ? rawPropertyId.toString()
          : String(rawPropertyId || '');
        if (!propertyId || pendingMap[propertyId]) return;
        pendingMap[propertyId] = {
          requestId: request._id,
          status: request.status || 'pending',
          createdAt: request.createdAt,
          newManagerName: request?.newManager?.name || ''
        };
      });
      setPendingManagerRequests(pendingMap);
    } catch (error) {
      console.error('Failed to fetch pending manager requests:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchPendingManagerRequests();
  }, [fetchPendingManagerRequests]);

  const propertyOptions = useMemo(() => {
    return (myProperties || []).map(({ property, units }) => {
      const id = normalizeId(getPropertyId(property));
      if (!id) return null;
      const isLocked = !!(property?.locked || property?.managementCompany?.locked);
      return { id, property, units: units || [], isLocked };
    }).filter(Boolean);
  }, [myProperties]);

  const getRedirectStatus = (property) => {
    if (!property) return false;
    return !!property.redirectLandlordIncomeToManager;
  };

  const companyOptions = useMemo(() => {
    const map = new Map();
    propertyOptions.forEach(({ id, property, isLocked }) => {
      const company = property?.managementCompany;
      const companyId = normalizeId(company?._id || company);
      if (!companyId) return;
      const entry = map.get(companyId) || {
        companyId,
        companyName: company?.companyName || 'Unnamed Company',
        properties: []
      };
      entry.properties.push({
        id,
        isLocked,
        redirect: getRedirectStatus(property),
        property
      });
      map.set(companyId, entry);
    });
    return Array.from(map.values())
      .map(entry => {
        const total = entry.properties.length;
        const onCount = entry.properties.filter(p => p.redirect).length;
        const lockedCount = entry.properties.filter(p => p.isLocked).length;
        const eligibleProperties = entry.properties.filter(p => !p.isLocked);
        const eligibleOnCount = eligibleProperties.filter(p => p.redirect).length;
        const eligibleOffCount = eligibleProperties.length - eligibleOnCount;
        let status = 'blocked';
        if (eligibleProperties.length > 0) {
          if (eligibleOnCount === eligibleProperties.length) status = 'on';
          else if (eligibleOnCount === 0) status = 'off';
          else status = 'mixed';
        }
        return {
          ...entry,
          total,
          onCount,
          offCount: total - onCount,
          lockedCount,
          eligiblePropertyCount: eligibleProperties.length,
          eligibleOnCount,
          eligibleOffCount,
          status
        };
      })
      .sort((a, b) => a.companyName.localeCompare(b.companyName));
  }, [propertyOptions]);

  useEffect(() => {
    const validPropertyIds = new Set(propertyOptions.map(option => option.id));
    const validCompanyIds = new Set(companyOptions.map(option => option.companyId));
    setBulkManagerSelection(prev => prev.filter((id) => {
      const normalizedId = normalizeId(id);
      return validPropertyIds.has(normalizedId) && !pendingManagerRequests[normalizedId];
    }));
    setBulkSettlementSelection(prev => prev.filter(id => validCompanyIds.has(normalizeId(id))));
  }, [companyOptions, propertyOptions, pendingManagerRequests]);

  const filterPropertyOptions = (options, search) => {
    const term = (search || '').trim().toLowerCase();
    if (!term) return options;
    return options.filter(({ property }) => {
      const name = (property?.name || '').toLowerCase();
      const address = (property?.address || '').toLowerCase();
      const company = (property?.managementCompany?.companyName || '').toLowerCase();
      const manager = (property?.manager?.name || '').toLowerCase();
      return name.includes(term) || address.includes(term) || company.includes(term) || manager.includes(term);
    });
  };

  const filterCompanyOptions = (options, search) => {
    const term = (search || '').trim().toLowerCase();
    if (!term) return options;
    return options.filter(({ companyName }) => (companyName || '').toLowerCase().includes(term));
  };

  const loadAvailableCompanies = async () => {
    try {
      setAvailableCompaniesLoading(true);
      const res = await api.get('/frp/available-managers');
      const list = Array.isArray(res.data?.managers) ? res.data.managers : [];
      list.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
      setAvailableCompanies(list);
      return true;
    } catch (err) {
      console.error('Failed to load companies:', err);
      alert('Failed to load available companies');
      return false;
    } finally {
      setAvailableCompaniesLoading(false);
    }
  };

  const ensureAvailableCompanies = async () => {
    if (availableCompaniesLoading) return false;
    if (availableCompanies.length === 0) {
      return await loadAvailableCompanies();
    }
    return true;
  };

  const toggleBulkManagerSelection = (companyId) => {
    setBulkManagerSelection((prev) => {
      const normalized = normalizeId(companyId);
      return prev.includes(normalized)
        ? prev.filter(id => id !== normalized)
        : [...prev, normalized];
    });
  };

  const toggleBulkSettlementSelection = (companyId) => {
    setBulkSettlementSelection((prev) => {
      const normalized = normalizeId(companyId);
      return prev.includes(normalized)
        ? prev.filter(id => id !== normalized)
        : [...prev, normalized];
    });
  };

  const openBulkSettlementPanel = () => {
    setShowBulkSettlementPanel(true);
    window.requestAnimationFrame(() => {
      bulkSettlementPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const applyBulkManagerChange = async () => {
    if (!bulkManagerCompanyId) {
      alert('Please select a new company.');
      return;
    }
    if (bulkManagerSelection.length === 0) {
      alert('Please select at least one company.');
      return;
    }
    const targetCompany = availableCompanies.find(c => normalizeId(c.companyId) === normalizeId(bulkManagerCompanyId));
    if (!targetCompany || !targetCompany._id) {
      alert('Selected company is not available.');
      return;
    }
    const newManagerId = targetCompany._id;
    const companyName = targetCompany.companyName || targetCompany.name || 'the selected company';
    const targetCompanyId = normalizeId(targetCompany.companyId);
    const targetPropertyIds = bulkManagerSelection
      .map(id => normalizeId(id))
      .filter(Boolean);
    const pendingIds = [];
    const uniquePropertyIds = Array.from(new Set(targetPropertyIds))
      .filter(id => {
        if (pendingManagerRequests[id]) {
          pendingIds.push(id);
          return false;
        }
        const entry = propertyOptions.find(p => normalizeId(p.id) === id);
        if (!entry || entry.isLocked) return false;
        const currentCompanyId = normalizeId(entry.property?.managementCompany?._id || entry.property?.managementCompany);
        return currentCompanyId !== targetCompanyId;
      });
    if (uniquePropertyIds.length === 0) {
      alert(pendingIds.length > 0
        ? 'Selected properties already have pending manager change requests.'
        : 'No eligible properties found for the selected companies.');
      return;
    }
    if (!window.confirm(`Send company change requests to ${companyName} for ${uniquePropertyIds.length} properties? The new company admin must accept each request.`)) {
      return;
    }
    setBulkManagerLoading(true);
    try {
      const results = await Promise.allSettled(
        uniquePropertyIds.map(propertyId =>
          api.post(`/frp/properties/${propertyId}/request-manager-change`, {
            newManagerId,
            message: bulkManagerMessage || ''
          })
        )
      );
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failureCount = results.length - successCount;
      alert(`Sent ${successCount} request(s).${failureCount ? ` ${failureCount} failed.` : ''}`);
      await fetchPendingManagerRequests();
    } catch (err) {
      console.error('Bulk company change failed:', err);
      alert('Failed to send company change requests.');
    } finally {
      setBulkManagerLoading(false);
    }
  };

  const applyBulkSettlementToggle = async (enabled, selectedCompanies) => {
    setBulkSettlementLoading(true);
    try {
      const selectedCompanyIds = new Set(selectedCompanies.map(id => normalizeId(id)));
      const targetPropertyIds = companyOptions
        .filter(company => selectedCompanyIds.has(normalizeId(company.companyId)))
        .flatMap(company =>
          company.properties
            .filter(p => !p.isLocked && p.redirect !== enabled)
            .map(p => p.id)
        );
      const uniquePropertyIds = Array.from(new Set(targetPropertyIds.map(id => normalizeId(id))));
      if (uniquePropertyIds.length === 0) {
        alert('No eligible properties found for the selected companies.');
        return;
      }
      const results = await Promise.allSettled(
        uniquePropertyIds.map(propertyId =>
          api.put(`/frp/properties/${propertyId}/redirect-income`, { enabled })
        )
      );
      const successIds = results
        .map((result, idx) => (result.status === 'fulfilled' ? uniquePropertyIds[idx] : null))
        .filter(Boolean)
        .map(id => normalizeId(id));

      if (successIds.length > 0) {
        setMyProperties(prev => prev.map(entry => {
          const propId = normalizeId(getPropertyId(entry.property));
          if (!successIds.includes(propId)) return entry;
          return {
            ...entry,
            property: {
              ...entry.property,
              redirectLandlordIncomeToManager: enabled
            }
          };
        }));
      }

      const failureCount = results.filter(r => r.status === 'rejected').length;
      alert(`Updated ${successIds.length} properties.${failureCount ? ` ${failureCount} failed.` : ''}`);
    } catch (err) {
      console.error('Bulk settlement update failed:', err);
      alert('Failed to update landlord settlement settings.');
    } finally {
      setBulkSettlementLoading(false);
    }
  };

  // Compute per-unit balances via tenant-view statements for accurate display
  useEffect(() => {
    let cancelled = false;
    const fetchBalances = async () => {
      setUnitBalancesLoading(true);
      if (!units || units.length === 0) {
        if (!cancelled) { setUnitBalances({}); setUnitUtilityBalances({}); }
        setUnitBalancesLoading(false);
        return;
      }
      try {
        const propId = selectedProperty?._id || (units[0]?.property?._id || units[0]?.property);
        if (!propId) {
          setUnitBalancesLoading(false);
          return;
        }
        const resp = await api.get(`/frp/properties/${propId}/unit-tenant-balances`);
        const map = {};
        const utilMap = {};
        // First, populate from API response (invoices - payments + unit.balance for units with transactions)
        for (const row of (resp.data?.balances || [])) {
          const uid = row.unitId?._id || row.unitId;
          map[uid] = typeof row.balance === 'number' ? row.balance : (parseFloat(row.balance) || 0);
          utilMap[uid] = typeof row.utilityBalance === 'number' ? row.utilityBalance : (parseFloat(row.utilityBalance) || 0);
        }
        // Then, for units not in API response (no transactions), use unit.balance directly
        for (const unit of units) {
          const uid = String(unit._id);
          if (map[uid] === undefined) {
            // Unit has no transactions, but may have a direct balance field
            const unitDirectBalance = unit?.balance ? (parseFloat(unit.balance) || 0) : 0;
            map[uid] = unitDirectBalance;
          }
        }
        if (!cancelled) { setUnitBalances(map); setUnitUtilityBalances(utilMap); }
      } catch (e) {
        // On error, fall back to unit.balance for all units
        const fallbackMap = {};
        for (const unit of units) {
          const uid = String(unit._id);
          fallbackMap[uid] = unit?.balance ? (parseFloat(unit.balance) || 0) : 0;
        }
        if (!cancelled) { setUnitBalances(fallbackMap); setUnitUtilityBalances({}); }
      }
      setUnitBalancesLoading(false);
    };
    fetchBalances();
    return () => { cancelled = true; };
  }, [units, selectedProperty]);

  const handlePropertyClick = (propertyData) => {
    setSelectedProperty(propertyData.property);
    setUnits(sortUnitsByLabel(propertyData.units));
    setShowUnitsView(true);
  };

  const handleBackToProperties = () => {
    setShowUnitsView(false);
    setSelectedProperty(null);
    setUnits([]);
  };

  // Fetch all payments and filter to a specific property (landlord view)
  const fetchPropertyPayments = async (propertyId) => {
    try {
      setPropertyPaymentsLoading(true);
      // Landlord-scoped payments only
      const response = await api.get("/frp/payments?scope=landlord");
      const allPayments = response.data.payments || [];
      console.log('All payments from /frp/payments:', allPayments);
      // Build a lookup map of payments by their ID so we can enrich
      // fallback statement rows with real split info from Payment model.
      const paymentById = {};
      allPayments.forEach((p) => {
        const id = p?._id || p?.id;
        if (id) {
          paymentById[String(id)] = p;
        }
      });
      const normalizeId = (val) => {
        const v = (val && (val._id || val.id || val)) || null;
        return v ? String(v) : null;
      };
      const targetPropertyId = normalizeId(propertyId);

      // Build set of unit IDs for this property
      const unitIds = Array.isArray(units) ? units.map(u => normalizeId(u._id)).filter(Boolean) : [];

      // Filter payments: match by property ID OR by unit ID
      let filtered = allPayments.filter((payment) => {
        const paymentPropertyId = normalizeId(payment?.unit?.property);
        const paymentUnitId = normalizeId(payment?.unit?._id || payment?.unit);
        const matchesProperty = paymentPropertyId && paymentPropertyId === targetPropertyId;
        const matchesUnit = unitIds.length > 0 && paymentUnitId && unitIds.includes(paymentUnitId);
        return matchesProperty || matchesUnit;
      });

      console.log('Filtered payments (primary filter):', filtered.length, 'of', allPayments.length);
      console.log('First filtered payment split:', filtered[0]?.split);

      // Deep fallback: if still empty, compile from per-unit statements
      if (!filtered.length && unitIds.length > 0) {
        try {
          const params = new URLSearchParams();
          if (propertyStatementStartDate) params.append('startDate', propertyStatementStartDate);
          if (propertyStatementEndDate) params.append('endDate', propertyStatementEndDate);
          const list = await Promise.all(
            units.map(async (u) => {
              try {
                const url = `/frp/units/${normalizeId(u._id)}/transaction-statement${params.toString() ? `?${params.toString()}` : ''}`;
                const res = await api.get(url);
                const st = res.data.statement;
                const unitLabel = st?.unitLabel || u.label;
                // Prefer a single tenant name if available, else first from list
                const tenantName = st?.tenantName || (Array.isArray(st?.tenantNames) ? st.tenantNames[0] : undefined);
                const paymentsOnly = (st?.transactions || []).filter(t => (t.paymentAmount || 0) > 0);
                return paymentsOnly.map(t => {
                  const paymentId = t.paymentId || t.paymentID || t.payment_id;
                  const fromPayment = paymentId ? paymentById[String(paymentId)] : undefined;
                  return {
                    date: t.date,
                    tenantDisplay: tenantName || 'Unknown',
                    unitLabel,
                    amount: t.paymentAmount,
                    description: t.description || 'Payment',
                    // Prefer split coming from the real Payment model; fall back to any split on the transaction itself.
                    split: (fromPayment && fromPayment.split) || t.split,
                  };
                });
              } catch (e) {
                console.warn('Fallback unit statement fetch failed for unit', u._id, e);
                return [];
              }
            })
          );
          // Flatten and project into the same shape expected by the table
          const flattened = list.flat().map((row) => ({
            _id: `${row.unitLabel}-${row.date}-${row.amount}`,
            date: row.date,
            tenant: { name: row.tenantDisplay },
            unit: { label: row.unitLabel },
            amount: row.amount,
            description: row.description,
            split: row.split,
          }));
          filtered = flattened;
        } catch (e) {
          console.warn('Deep fallback aggregation failed', e);
        }
      }
      setPropertyPayments(filtered);
    } catch (error) {
      console.error("Error fetching property payments:", error);
      alert("Error fetching property statements: " + (error.response?.data?.error || error.message));
    } finally {
      setPropertyPaymentsLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const fetchTransactionStatement = async (unit, startDate = "", endDate = "") => {
    try {
      setStatementLoading(true);

      // Build query parameters only if user provided filters; default shows all
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const queryString = params.toString();

      // Determine endpoint based on user's relationship to this specific unit
      let url;

      // Check if user has tenant access to this specific unit
      const isCurrentTenant = unit.tenant?._id === user?.id;
      const isFormerTenant = unit.tenantHistory?.some(th => th.tenant._id === user?.id);
      const hasDirectTenantAccess = isCurrentTenant || isFormerTenant;

      // Check if user has tenant role for this specific property
      const hasPropertyTenantRole = user?.propertyRoles?.some(pr =>
        pr.property === (unit.property._id || unit.property) && pr.role === 'T'
      );

      console.log('fetchTransactionStatement debug (landlord view):', {
        unitId: unit._id,
        propertyId: unit.property._id || unit.property,
        activeRole,
        isCurrentTenant,
        isFormerTenant,
        hasDirectTenantAccess,
        hasPropertyTenantRole,
        userId: user?.id || user?._id,
        userGlobalRole: user?.role,
        userPropertyRoles: user?.propertyRoles
      });

      // Use tenant endpoint if user has direct tenant access to this unit AND is viewing as tenant
      if ((hasDirectTenantAccess || hasPropertyTenantRole) && activeRole === 'tenant') {
        url = `/frp/units/${unit._id}/tenant-statements${queryString ? `?${queryString}` : ''}`;
        console.log('Using tenant-statements endpoint:', url);
      } else {
        // Use management/landlord endpoint for all other cases
        url = `/frp/units/${unit._id}/transaction-statement${queryString ? `?${queryString}` : ''}`;
        console.log('Using transaction-statement endpoint:', url);
      }

      const response = await api.get(url);
      setTransactionStatement(response.data.statement);
      setSelectedUnit(unit);
      setShowTransactionStatement(true);
    } catch (error) {
      // If tenant endpoint fails with access denied, try management endpoint as fallback
      if (error.response?.status === 403 && error.response?.data?.error?.includes('tenant access only')) {
        try {
          const params = new URLSearchParams();
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          const queryString = params.toString();

          const fallbackUrl = `/frp/units/${unit._id}/transaction-statement${queryString ? `?${queryString}` : ''}`;
          const response = await api.get(fallbackUrl);
          setTransactionStatement(response.data.statement);
          setSelectedUnit(unit);
          setShowTransactionStatement(true);
          return; // Success with fallback
        } catch (fallbackError) {
          // If fallback also fails, show the original error
          alert("Error fetching transaction statement: " + (error.response?.data?.error || error.message));
        }
      } else {
        alert("Error fetching transaction statement: " + (error.response?.data?.error || error.message));
      }
    } finally {
      setStatementLoading(false);
    }
  };

  const handleRedirectToggle = (property, newValue) => {
    setRedirectProperty(property);
    setRedirectActionValue(newValue);
    setShowRedirectModal(true);
  };

  const confirmRedirectChange = async () => {
    if (!redirectProperty) return;
    setRedirectLoading(true);
    try {
      await api.put(`/frp/properties/${redirectProperty._id}/redirect-income`, {
        enabled: redirectActionValue
      });

      // Update local state
      const updatedProp = { ...redirectProperty, redirectLandlordIncomeToManager: redirectActionValue };
      setSelectedProperty(updatedProp); // Update current view

      // Update list
      setMyProperties(prev => prev.map(p => {
        if (p.property._id === redirectProperty._id) {
          return { ...p, property: updatedProp };
        }
        return p;
      }));

      setShowRedirectModal(false);
      setRedirectProperty(null);
    } catch (error) {
      console.error("Error updating redirect settings:", error);
      alert("Failed to update settings: " + (error.response?.data?.error || error.message));
    } finally {
      setRedirectLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 py-2" aria-busy="true" aria-label="Loading properties">
        <SkeletonStatGrid count={3} />
        <SkeletonList rows={4} />
      </div>
    );
  }

  // Units View - When a property is selected
  if (showUnitsView && selectedProperty) {
    const selectedPropertyId = normalizeId(getPropertyId(selectedProperty));
    const selectedPropertyPendingRequest = pendingManagerRequests[selectedPropertyId] || null;

    return (
      <div>
        {/* Redirect Confirmation Modal */}
        <RedirectConfirmationModal
          show={showRedirectModal}
          onClose={() => setShowRedirectModal(false)}
          onConfirm={confirmRedirectChange}
          property={redirectProperty}
          value={redirectActionValue}
          loading={redirectLoading}
        />

        {/* Manager Change Modal */}
        {showManagerChangeModal && selectedProperty && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-orange-100 rounded-full mb-4">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-center text-gray-900 mb-2">
                  Request Company Change
                </h3>
                <p className="text-center text-gray-600 mb-4 text-sm">
                  Property: <span className="font-semibold text-green-600">{selectedProperty.name}</span>
                </p>
                <p className="text-center text-gray-500 mb-4 text-xs">
                  Current company: <span className="font-semibold text-gray-700">{selectedProperty.managementCompany?.companyName || 'None'}</span>
                </p>
                {selectedPropertyPendingRequest && (
                  <p className="text-center text-amber-700 mb-4 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    Request Sent / Pending
                  </p>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select New Company</label>
                    <select
                      value={selectedNewCompanyAdminId}
                      onChange={(e) => setSelectedNewCompanyAdminId(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">-- Select a Company --</option>
                      {availableCompanies
                        .filter(company => normalizeId(company.companyId) !== normalizeId(selectedProperty.managementCompany?._id || selectedProperty.managementCompany))
                        .map(company => (
                          <option key={`${company.companyId}-${company._id}`} value={company._id}>
                            {company.companyName}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
                    <textarea
                      value={managerChangeMessage}
                      onChange={(e) => setManagerChangeMessage(e.target.value)}
                      placeholder="Add a note for the new company..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                      rows={3}
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowManagerChangeModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                    disabled={managerChangeLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (selectedPropertyPendingRequest) {
                        alert('A manager change request is already pending for this property.');
                        return;
                      }
                      if (!selectedNewCompanyAdminId) {
                        alert('Please select a new company');
                        return;
                      }
                      setManagerChangeLoading(true);
                      try {
                        await api.post(`/frp/properties/${selectedProperty._id}/request-manager-change`, {
                          newManagerId: selectedNewCompanyAdminId,
                          message: managerChangeMessage
                        });
                        alert('Company change request sent successfully! The new company admin will need to accept.');
                        await fetchPendingManagerRequests();
                        setShowManagerChangeModal(false);
                      } catch (err) {
                        console.error('Company change request failed:', err);
                        alert('Failed to send request: ' + (err.response?.data?.error || err.message));
                      } finally {
                        setManagerChangeLoading(false);
                      }
                    }}
                    disabled={managerChangeLoading || !selectedNewCompanyAdminId || !!selectedPropertyPendingRequest}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {managerChangeLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Sending...</span>
                      </div>
                    ) : (
                      selectedPropertyPendingRequest ? 'Pending' : 'Send Request'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={handleBackToProperties}
            className="flex items-center gap-2 text-orange-600 hover:text-green-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Properties
          </button>
        </div>

        <div className="flex flex-col gap-6 mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Units List- <span className="text-green-600">{selectedProperty.name}</span>
            </h2>
            <p className="text-gray-600 mt-1">{selectedProperty.address}</p>
          </div>

          {/* Action Buttons Section */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
            {/* Income Redirection Toggle */}
            <div className="flex items-center justify-between lg:justify-start gap-4 w-full lg:w-auto">
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-800">Landlord's Settlement</span>
                <span className="text-[10px] text-gray-500">
                  Current:
                  {selectedProperty.redirectLandlordIncomeToManager
                    ? " External arrangement with Management"
                    : " Daily Bank Deposits from RecordLOGS"}
                </span>
              </div>
              <button
                onClick={() => handleRedirectToggle(selectedProperty, !selectedProperty.redirectLandlordIncomeToManager)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${selectedProperty.redirectLandlordIncomeToManager ? 'bg-green-600' : 'bg-gray-300'}`}
              >
                <span className={`${selectedProperty.redirectLandlordIncomeToManager ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              {/* Manager Actions Group */}
              <div className="grid grid-cols-2 sm:flex sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={async () => {
                    try {
                      const loaded = await ensureAvailableCompanies();
                      if (!loaded) return;
                      setSelectedNewCompanyAdminId('');
                      setManagerChangeMessage('Hi.\nI would like you to manage my property.');
                      setShowManagerChangeModal(true);
                    } catch (err) {
                      console.error('Failed to load companies:', err);
                      alert('Failed to load available companies');
                    }
                  }}
                  className="flex items-center justify-center gap-2 bg-white border border-orange-200 text-orange-700 hover:bg-orange-50 hover:border-orange-300 px-4 py-2.5 rounded-lg transition-all text-sm font-medium shadow-sm w-full sm:w-auto"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  <span className="sm:inline">Change Management</span>
                </button>

              </div>

              <button
                onClick={() => {
                  setShowPropertyStatementsModal(true);
                  const { start, end } = getCurrentMonthRange();
                  setPropertyStatementStartDate(start);
                  setPropertyStatementEndDate(end);
                  fetchPropertyPayments(selectedProperty._id);
                }}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2.5 rounded-lg hover:shadow-lg hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center justify-center gap-2 w-full sm:w-auto shadow-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a2 2 0 012-2h8m-6 8h6m-6-4h6M7 7h.01M4 17h.01" />
                </svg>
                Statements
              </button>
            </div>
          </div>
        </div>

        {
          units.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-500">No units found for this property.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200">
              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Name</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Tenant</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">ID Number</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Balance Due</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">UT-Balance</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {units.map((unit) => (
                      <tr
                        key={unit._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <span className="text-base font-medium text-gray-900">{unit.label}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            {unit.tenants && unit.tenants.length > 0 ? (
                              <div className="space-y-1">
                                {unit.tenants.map((tenant, index) => (
                                  <div key={index} className="text-gray-900">
                                    {tenant.user?.name || tenant.user?.email || "Unknown"}
                                  </div>
                                ))}
                              </div>
                            ) : unit.tenant ? (
                              <div className="text-gray-900">
                                {unit.tenant.name || unit.tenant.email || "Unknown"}
                              </div>
                            ) : (
                              <span className="text-red-600 font-medium">Vacant</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            {unit.tenants && unit.tenants.length > 0 ? (
                              <div className="space-y-1">
                                {unit.tenants.map((tenant, index) => (
                                  <div key={index} className="text-gray-900">
                                    {tenant.user?.idNumber || "N/A"}
                                  </div>
                                ))}
                              </div>
                            ) : unit.tenant ? (
                              <div className="text-gray-900">
                                {unit.tenant.idNumber || "N/A"}
                              </div>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-900 font-medium">
                            {(() => {
                              const balance = unitBalances[unit._id];
                              if (typeof balance !== 'number') {
                                return <span className="text-gray-400">—</span>;
                              }
                              return balance > 0
                                ? <span className="text-red-600">{formatMoney(balance)}</span>
                                : balance < 0
                                  ? <span className="text-green-600">-{formatMoney(Math.abs(balance))}</span>
                                  : <span className="text-green-600">{formatMoney(0)}</span>;
                            })()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-900 font-medium">
                            {(() => {
                              const utilBalance = unitUtilityBalances[unit._id];
                              if (typeof utilBalance !== 'number') {
                                return <span className="text-gray-400">—</span>;
                              }
                              return utilBalance > 0
                                ? <span className="text-red-600">{formatMoney(utilBalance)}</span>
                                : utilBalance < 0
                                  ? <span className="text-green-600">-{formatMoney(Math.abs(utilBalance))}</span>
                                  : <span className="text-green-600">{formatMoney(0)}</span>;
                            })()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => {
                              setSelectedUnit(unit);
                              setShowUnitDetailsModal && setShowUnitDetailsModal(true);
                            }}
                            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg hover:shadow-lg hover:shadow-green-500/25 transition-all duration-200 font-medium"
                          >
                            Open/View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Table View - Fit to Screen */}
              <div className="">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-0.5 py-2 text-[10px] font-medium text-gray-700 break-words leading-tight" style={{ width: "11%" }}>Name</th>
                      <th className="text-left px-0.5 py-2 text-[10px] font-medium text-gray-700 break-words leading-tight" style={{ width: "25%" }}>Tenant</th>
                      <th className="text-left px-0.5 py-2 text-[10px] font-medium text-gray-700 break-words leading-tight" style={{ width: "16%" }}>Rent</th>
                      <th className="text-left px-0.5 py-2 text-[10px] font-medium text-gray-700 break-words leading-tight" style={{ width: "16%" }}>Balance</th>
                      <th className="text-left px-0.5 py-2 text-[10px] font-medium text-gray-700 break-words leading-tight" style={{ width: "16%" }}>UT-Balance</th>
                      <th className="text-left px-0.5 py-2 text-[10px] font-medium text-gray-700 break-words leading-tight" style={{ width: "16%" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {units.map((unit) => (
                      <tr
                        key={unit._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-0.5 py-2" style={{ width: "11%" }}>
                          <span className="text-[10px] font-medium text-gray-900 break-words leading-tight">{unit.label}</span>
                        </td>
                        <td className="px-0.5 py-2" style={{ width: "25%" }}>
                          <div className="text-[10px]">
                            {unit.tenants && unit.tenants.length > 0 ? (
                              <div>
                                {unit.tenants.map((tenant, index) => {
                                  const tenantName = tenant.user?.name || tenant.user?.email?.split('@')[0] || "Unknown";
                                  const idNumber = tenant.user?.idNumber;
                                  return (
                                    <div key={index} className="text-gray-900 break-words leading-tight">
                                      {tenantName}{idNumber ? ` - #${idNumber}` : ""}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : unit.tenant ? (
                              <div className="text-gray-900 break-words leading-tight">
                                {(unit.tenant.name || unit.tenant.email?.split('@')[0] || "Unknown")}{unit.tenant.idNumber ? ` - #${unit.tenant.idNumber}` : ""}
                              </div>
                            ) : (
                              <span className="text-red-600 font-medium text-[10px]">Vacant</span>
                            )}
                          </div>
                        </td>
                        <td className="px-0.5 py-2" style={{ width: "16%" }}>
                          <span className="text-[10px] font-medium break-words leading-tight">{formatMoney(unit.rentAmount || 0)}</span>
                        </td>
                        <td className="px-0.5 py-2" style={{ width: "16%" }}>
                          <span className="text-[10px] font-medium break-words leading-tight">
                            {(() => {
                              const balance = unitBalances[unit._id];
                              if (typeof balance !== 'number') {
                                return <span className="text-gray-400">—</span>;
                              }
                              return balance > 0
                                ? <span className="text-red-600">{formatMoney(balance)}</span>
                                : balance < 0
                                  ? <span className="text-green-600">-{formatMoney(Math.abs(balance))}</span>
                                  : <span className="text-green-600">{formatMoney(0)}</span>;
                            })()}
                          </span>
                        </td>
                        <td className="px-0.5 py-2" style={{ width: "16%" }}>
                          <span className="text-[10px] font-medium break-words leading-tight">
                            {(() => {
                              const utilBalance = unitUtilityBalances[unit._id];
                              if (typeof utilBalance !== 'number') {
                                return <span className="text-gray-400">—</span>;
                              }
                              return utilBalance > 0
                                ? <span className="text-red-600">{formatMoney(utilBalance)}</span>
                                : utilBalance < 0
                                  ? <span className="text-green-600">-{formatMoney(Math.abs(utilBalance))}</span>
                                  : <span className="text-green-600">{formatMoney(0)}</span>;
                            })()}
                          </span>
                        </td>
                        <td className="px-0.5 py-2" style={{ width: "16%" }}>
                          <button
                            onClick={() => {
                              setSelectedUnit(unit);
                              setShowUnitDetailsModal && setShowUnitDetailsModal(true);
                            }}
                            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-0.5 py-1 rounded text-[10px] font-medium hover:shadow-md transition-all duration-200 w-full"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        }

        {
          showPropertyStatementsModal && selectedProperty && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
              <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="bg-gradient-to-r from-green-600 to-green-700 p-4 sm:p-6 text-white flex-shrink-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-1">Property Statements</h3>
                      <div className="text-sm opacity-90">
                        <span className="font-medium">{selectedProperty.name}</span> •
                        <span> Payments by tenants</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowPropertyStatementsModal(false);
                        setPropertyStatementStartDate("");
                        setPropertyStatementEndDate("");
                        setPropertyPayments([]);
                      }}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                  <button
                    onClick={() => setPropertyFilterOpen(prev => !prev)}
                    className="w-full flex items-center justify-between text-gray-800 font-medium"
                  >
                    <span>Filter</span>
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {propertyFilterOpen && (
                    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                      <div className="flex-1">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={propertyStatementStartDate}
                          onChange={(e) => setPropertyStatementStartDate(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={propertyStatementEndDate}
                          onChange={(e) => setPropertyStatementEndDate(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button
                          onClick={() => fetchPropertyPayments(selectedProperty._id)}
                          disabled={propertyPaymentsLoading}
                          className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                        >
                          {propertyPaymentsLoading ? 'Refreshing...' : 'Refresh'}
                        </button>
                        <button
                          onClick={() => { setPropertyStatementStartDate(""); setPropertyStatementEndDate(""); }}
                          className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto">
                  <div className="p-4 sm:p-6">
                    {propertyPaymentsLoading ? (
                      <div className="flex items-center justify-center py-8 text-gray-600">Loading statements...</div>
                    ) : (() => {
                      const start = propertyStatementStartDate ? new Date(propertyStatementStartDate) : null;
                      const end = propertyStatementEndDate ? new Date(propertyStatementEndDate) : null;
                      if (start) { start.setHours(0, 0, 0, 0); }
                      if (end) { end.setHours(23, 59, 59, 999); }
                      const filtered = propertyPayments.filter(p => {
                        const d = new Date(p.date);
                        if (start && d < start) return false;
                        if (end && d > end) return false;
                        return true;
                      });
                      // Totals across filtered rows using raw split amounts
                      const totals = filtered.reduce((acc, p) => {
                        const split = p?.split || {};
                        const amount = Number(p?.amount || 0);
                        const platformAmount = Number(split.platformAmount ?? 0);
                        const managerAmount = Number(split.managerAmount ?? 0);
                        const landlordAmount = Number(split.landlordAmount ?? 0);
                        acc.amount += amount;
                        acc.platform += platformAmount;
                        acc.manager += managerAmount;
                        acc.landlord += landlordAmount;
                        return acc;
                      }, { amount: 0, platform: 0, manager: 0, landlord: 0 });
                      const totalAmount = totals.amount || 0;
                      return filtered.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full table-fixed border border-emerald-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                            <colgroup>
                              <col className="w-[18%] sm:w-[20%]" />
                              <col className="w-[18%] sm:w-[20%]" />
                              <col className="w-[18%] sm:w-[20%]" />
                              <col className="w-[18%] sm:w-[20%]" />
                              <col className="w-[20%] sm:w-[20%]" />
                              <col className="w-[20%] sm:w-[20%]" />
                              <col className="w-[16%] sm:w-[10%]" />
                            </colgroup>
                            <thead className="bg-emerald-600 text-white">
                              <tr className="text-center">
                                <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                                <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Tenant</th>
                                <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Unit</th>
                                <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Amount</th>
                                <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Portal %</th>
                                <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Mgmt %</th>
                                <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">LL %</th>
                              </tr>
                              <tr className="bg-emerald-50 text-emerald-900">
                                <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-[9px] sm:text-xs">Total</td>
                                <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-[9px] sm:text-xs">Across selected period</td>
                                <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-[9px] sm:text-xs"></td>
                                <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-[8px] sm:text-[10px] leading-tight break-words whitespace-normal text-right">{formatMoney(totalAmount)}</td>
                                <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-[8px] sm:text-[10px] leading-tight break-words whitespace-normal text-right">{formatMoney(totals.platform)}</td>
                                <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-[8px] sm:text-[10px] leading-tight break-words whitespace-normal text-right">{formatMoney(totals.manager)}</td>
                                <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-[8px] sm:text-[10px] leading-tight break-words whitespace-normal text-right">{formatMoney(totals.landlord)}</td>
                              </tr>
                            </thead>
                            <tbody>
                              {filtered.map((payment, idx) => {
                                const split = payment?.split || {};
                                console.log(payment);
                                const hasSplit =
                                  split &&
                                  (split.platformAmount != null ||
                                    split.managerAmount != null ||
                                    split.landlordAmount != null);
                                const platformAmount = hasSplit ? Number(split.platformAmount) : 0;
                                const managerAmount = hasSplit ? Number(split.managerAmount) : 0;
                                const landlordAmount = hasSplit ? Number(split.landlordAmount) : 0;
                                return (
                                  <tr key={payment._id || idx} className="odd:bg-white even:bg-emerald-50/40">
                                    <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-gray-800 whitespace-normal break-words">
                                      <div className="block sm:hidden break-inside-avoid">
                                        {formatDateWithTime(payment.date)}
                                      </div>
                                      <div className="hidden sm:block break-inside-avoid">
                                        {formatDateWithTime(payment.date)}
                                      </div>
                                    </td>
                                    <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-gray-800 align-top break-words whitespace-normal leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>
                                      {payment?.tenant?.name || payment?.tenant?.email || 'Unknown'}
                                    </td>
                                    <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-gray-800 align-top whitespace-nowrap">
                                      {payment?.unit?.label || '-'}
                                    </td>
                                    <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-gray-800 align-top text-right whitespace-nowrap">
                                      {formatMoney(payment?.amount || 0)}
                                    </td>
                                    <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-gray-800 align-top text-right whitespace-nowrap">
                                      {hasSplit ? formatMoney(platformAmount) : '—'}
                                    </td>
                                    <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-gray-800 align-top text-right whitespace-nowrap">
                                      {hasSplit ? formatMoney(managerAmount) : '—'}
                                    </td>
                                    <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-gray-800 align-top text-right whitespace-nowrap">
                                      {hasSplit ? formatMoney(landlordAmount) : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p className="text-sm">No payments found for this property.</p>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
                  <button
                    onClick={() => setShowPropertyStatementsModal(false)}
                    className="w-full sm:w-auto px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )
        }
      </div >
    );
  }

  // Properties View - Default view
  return (
    <div>
      {/* Redirect Confirmation Modal */}
      <RedirectConfirmationModal
        show={showRedirectModal}
        onClose={() => setShowRedirectModal(false)}
        onConfirm={confirmRedirectChange}
        property={redirectProperty}
        value={redirectActionValue}
        loading={redirectLoading}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {/* Quick Summary Card */}
        <div className="order-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">My Properties</p>
            <p className="text-2xl font-bold text-gray-900">{myProperties.length}</p>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            <span>All properties active</span>
          </div>
        </div>

        {/* Action Card */}
        <div className="order-1 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 shadow-md text-white flex flex-col justify-between">
          <div>
            <p className="text-sm font-medium opacity-90">Landlord's Actions</p>
            <p className="text-lg font-bold mt-1">Controls</p>
          </div>
          <button 
            onClick={openBulkSettlementPanel}
            className="mt-4 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm font-bold transition-colors backdrop-blur-sm"
          >
            Open Bulk Settlement
          </button>
        </div>
      </div>

      <div className="bg-white border border-blue-100 rounded-2xl shadow-sm p-5 mb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Bills &amp; Invoices Wallets</p>
            <p className="text-2xl font-bold text-blue-800">
              {utilityWalletsLoading ? 'Loading...' : formatMoney(utilityWalletBalanceTotal)}
            </p>
          </div>
          <button
            onClick={fetchUtilityWallets}
            disabled={utilityWalletsLoading}
            className="self-start rounded-lg border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-60"
          >
            Refresh
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {utilityWallets.length === 0 ? (
            <p className="text-sm text-gray-500">No utility income yet.</p>
          ) : (
            utilityWallets.map((wallet) => (
              <div key={wallet._id} className="flex items-center justify-between gap-3 rounded-xl bg-blue-50 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {wallet?.property?.name || 'Property'}
                  </p>
                  <p className="text-xs text-gray-500">{formatMoney(getUtilityWalletBalance(wallet))}</p>
                </div>
                <button
                  onClick={() => openUtilityStatement(wallet)}
                  className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                >
                  Statement
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="flex flex-col items-center gap-6 lg:grid lg:grid-cols-2 mb-8">
        <div ref={bulkSettlementPanelRef} className="bg-white border border-gray-200 rounded-2xl shadow-sm w-full max-w-2xl lg:max-w-none">
          <button
            onClick={() => setShowBulkManagerPanel(prev => !prev)}
            className="w-full flex items-center justify-between px-5 py-4"
          >
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900">Switch Company</h3>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">{bulkManagerSelection.length} selected</span>
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${showBulkManagerPanel ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {showBulkManagerPanel && (
            <div className="px-5 pb-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm text-gray-600">
                  1. Select New Management Company. <br />
                  2. Select Properties to switch <br />
                  3. Send Change requests
                </div>
                <button
                  onClick={loadAvailableCompanies}
                  disabled={availableCompaniesLoading}
                  className="text-xs px-3 py-1 rounded-full border border-green-200 text-green-700 hover:bg-green-50 disabled:opacity-60"
                >
                  {availableCompaniesLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              <div>
                <SearchableDropdown
                  label="New Company"
                  options={availableCompanies
                    .filter(company => !(company?.locked || company?.isLocked || company?.companyLocked))
                    .map(company => ({
                      value: company.companyId,
                      label: company.companyName
                    }))}
                  value={bulkManagerCompanyId}
                  onChange={(value) => setBulkManagerCompanyId(value)}
                  onFocus={ensureAvailableCompanies}
                  placeholder="Select company"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
                <textarea
                  value={bulkManagerMessage}
                  onChange={(e) => setBulkManagerMessage(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none text-sm"
                  placeholder="Add a note for the new company..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Select properties</span>
                  <div className="flex items-center gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setBulkManagerSelection(
                        propertyOptions
                          .filter((p) => !p.isLocked && !pendingManagerRequests[normalizeId(p.id)])
                          .map(p => p.id)
                      )}
                      className="px-2 py-1 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkManagerSelection([])}
                      className="px-2 py-1 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="relative mb-2">
                  <input
                    type="text"
                    value={bulkManagerSearch}
                    onChange={(e) => setBulkManagerSearch(e.target.value)}
                    placeholder="Search properties..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                  {filterPropertyOptions(propertyOptions, bulkManagerSearch).length === 0 ? (
                    <div className="text-sm text-gray-500 p-2">No matching properties.</div>
                  ) : (
                    filterPropertyOptions(propertyOptions, bulkManagerSearch).map(({ id, property, isLocked }) => {
                      const statusLabel = getRedirectStatus(property) ? 'Redirect ON' : 'Redirect OFF';
                      const isPendingRequest = !!pendingManagerRequests[normalizeId(id)];
                      const isDisabled = isLocked || isPendingRequest;
                      return (
                        <label
                          key={id}
                          className={`flex items-start gap-2 p-2 rounded-md ${isDisabled ? 'opacity-60' : 'hover:bg-white'}`}
                        >
                          <input
                            type="checkbox"
                            disabled={isDisabled}
                            checked={bulkManagerSelection.includes(id)}
                            onChange={() => toggleBulkManagerSelection(id)}
                            className="mt-1"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-800 truncate">{property?.name || 'Unnamed property'}</div>
                            <div className="text-xs text-gray-500 truncate">{property?.address || 'No address'}</div>
                            {isPendingRequest && (
                              <div className="mt-1 text-[10px] inline-flex items-center border border-amber-200 text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">
                                Request Sent / Pending
                              </div>
                            )}
                            <div className="text-xs text-gray-500 truncate">
                              Company: {property?.managementCompany?.companyName || 'None'} • {statusLabel}
                            </div>
                          </div>
                          {isLocked && (
                            <span className="text-[10px] border border-red-200 text-red-600 rounded px-1 py-0.5">Blocked</span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {bulkManagerSelection.length} selected
                </div>
              </div>

              <button
                type="button"
                onClick={applyBulkManagerChange}
                disabled={bulkManagerLoading}
                className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-60"
              >
                {bulkManagerLoading ? 'Sending...' : 'Send Change Requests'}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm w-full max-w-2xl lg:max-w-none">
          <button
            onClick={() => setShowBulkSettlementPanel(prev => !prev)}
            className="w-full flex items-center justify-between px-5 py-4"
          >
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900">Landlord's Settlement</h3>
              <p className="text-sm text-gray-600">Toggle which companies redirect landlord payments.</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">{bulkSettlementSelection.length} selected</span>
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${showBulkSettlementPanel ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {showBulkSettlementPanel && (
            <div className="px-5 pb-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Select companies</span>
                <div className="flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setBulkSettlementSelection(companyOptions.filter(c => c.eligiblePropertyCount > 0).map(c => c.companyId))}
                    className="px-2 py-1 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkSettlementSelection([])}
                    className="px-2 py-1 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="relative">
                <input
                  type="text"
                  value={bulkSettlementSearch}
                  onChange={(e) => setBulkSettlementSearch(e.target.value)}
                  placeholder="Search companies by name..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {(() => {
                const filtered = filterCompanyOptions(companyOptions, bulkSettlementSearch);
                const redirectOn = filtered.filter(company => company.eligibleOnCount > 0);
                const redirectOff = filtered.filter(company => company.eligibleOffCount > 0);

                // Render function with search and pagination
                const renderList = (items, label, accent, searchValue, setSearchValue, showCount, setShowCount) => {
                  // Filter items by the section-specific search
                  const searchFiltered = items.filter(item =>
                    item.companyName.toLowerCase().includes(searchValue.toLowerCase())
                  );

                  // Slice to show only the specified count
                  const displayedItems = searchFiltered.slice(0, showCount);
                  const hasMore = searchFiltered.length > showCount;

                  return (
                    <div className="space-y-2">
                      <div className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>{label}</div>

                      {/* Search Input */}
                      <div className="relative">
                        <input
                          type="text"
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          placeholder="Search by name..."
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>

                      {/* Companies List */}
                      <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                        {displayedItems.length === 0 ? (
                          <div className="text-sm text-gray-500 p-2">
                            {searchValue ? 'No matching companies found.' : 'No companies found.'}
                          </div>
                        ) : (
                          <>
                            {displayedItems.map((company) => {
                              const isDisabled = company.eligiblePropertyCount === 0;
                              const statusLabel =
                                company.status === 'on'
                                  ? 'Mgmt Externally'
                                  : company.status === 'off'
                                    ? 'RecordLOGS'
                                    : company.status === 'mixed'
                                      ? 'Mixed'
                                      : 'Blocked';
                              return (
                                <label
                                  key={company.companyId}
                                  className={`flex items-start gap-2 p-2 rounded-md ${isDisabled ? 'opacity-60' : 'hover:bg-white'}`}
                                >
                                  <input
                                    type="checkbox"
                                    disabled={isDisabled}
                                    checked={bulkSettlementSelection.includes(company.companyId)}
                                    onChange={() => toggleBulkSettlementSelection(company.companyId)}
                                    className="mt-1"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-gray-800 truncate">{company.companyName}</div>
                                    <div className="text-xs text-gray-500">
                                      Settlement: {statusLabel}
                                    </div>
                                  </div>
                                  {isDisabled && (
                                    <span className="text-[10px] border border-red-200 text-red-600 rounded px-1 py-0.5">Blocked</span>
                                  )}
                                </label>
                              );
                            })}

                            {/* Show More Button */}
                            {hasMore && (
                              <button
                                type="button"
                                onClick={() => setShowCount(prev => prev + 5)}
                                className="w-full mt-2 px-3 py-2 text-sm text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors font-medium"
                              >
                                Show more ({searchFiltered.length - showCount} remaining)
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                };

                const openConfirm = (action, companies) => {
                  if (companies.length === 0) {
                    alert('Please select at least one company in this section.');
                    return;
                  }
                  setSettlementConfirmAction(action);
                  setSettlementConfirmCompanies(companies);
                  setShowSettlementConfirm(true);
                };

                const selectedOffCompanies = redirectOff
                  .filter(c => bulkSettlementSelection.map(id => normalizeId(id)).includes(normalizeId(c.companyId)))
                  .map(c => c.companyId);
                const selectedOnCompanies = redirectOn
                  .filter(c => bulkSettlementSelection.map(id => normalizeId(id)).includes(normalizeId(c.companyId)))
                  .map(c => c.companyId);

                return (
                  <div className="space-y-6">
                    {/* Action Buttons Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => openConfirm('on', selectedOffCompanies)}
                        className="flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg bg-green-600 text-white text-[11px] sm:text-sm leading-tight font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={bulkSettlementLoading || selectedOffCompanies.length === 0}
                      >
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                        <span>Settle with Management Externally</span>
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="bg-green-700 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs">Selected {selectedOffCompanies.length}</span>
                          <span className="text-[10px] sm:text-xs">SAVE</span>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => openConfirm('off', selectedOnCompanies)}
                        className="flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg bg-gray-600 text-white text-[11px] sm:text-sm leading-tight font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={bulkSettlementLoading || selectedOnCompanies.length === 0}
                      >
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span>RecordLOGS Deposit Daily</span>
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="bg-gray-700 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs">Selected {selectedOnCompanies.length}</span>
                          <span className="text-[10px] sm:text-xs">SAVE</span>
                        </div>
                      </button>
                    </div>

                    {/* Company Lists */}
                    <div className="space-y-3">
                      {renderList(
                        redirectOff,
                        'Payments to landlord',
                        'text-gray-500',
                        redirectOffSearch,
                        setRedirectOffSearch,
                        redirectOffShowCount,
                        setRedirectOffShowCount
                      )}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{selectedOffCompanies.length} selected</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {renderList(
                        redirectOn,
                        'Payments to manager',
                        'text-green-600',
                        redirectOnSearch,
                        setRedirectOnSearch,
                        redirectOnShowCount,
                        setRedirectOnShowCount
                      )}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{selectedOnCompanies.length} selected</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {showSettlementConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-center text-gray-900 mb-2">
                Confirm Settlement Change
              </h3>
              <p className="text-center text-gray-600 mb-4 text-sm">
                You are about to turn <span className="font-semibold text-green-700">{settlementConfirmAction === 'on' ? 'ON' : 'OFF'}</span> redirect for {settlementConfirmCompanies.length} companies.
              </p>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 text-sm text-gray-600 mb-5">
                {settlementConfirmCompanies.map(companyId => {
                  const company = companyOptions.find(c => normalizeId(c.companyId) === normalizeId(companyId));
                  return (
                    <div key={companyId} className="py-1 border-b border-gray-100 last:border-b-0">
                      {company?.companyName || 'Unnamed Company'}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSettlementConfirm(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  disabled={bulkSettlementLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const enabled = settlementConfirmAction === 'on';
                    const targets = settlementConfirmCompanies.slice();
                    setShowSettlementConfirm(false);
                    await applyBulkSettlementToggle(enabled, targets);
                  }}
                  disabled={bulkSettlementLoading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  {bulkSettlementLoading ? 'Updating...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUtilityStatementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Bills &amp; Invoices Statement</h3>
                <p className="text-sm text-gray-500">
                  {selectedUtilityWallet?.property?.name || 'Property'} - Balance {formatMoney(getUtilityWalletBalance(selectedUtilityWallet || {}))}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowUtilityStatementModal(false);
                  setSelectedUtilityWallet(null);
                  setUtilityTransactions([]);
                }}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="max-h-[65vh] overflow-y-auto p-5">
              {utilityTransactionsLoading ? (
                <p className="py-8 text-center text-sm text-gray-500">Loading statement...</p>
              ) : utilityTransactions.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">No utility transactions yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                        <th className="py-2 pr-3">Date</th>
                        <th className="py-2 pr-3">Description</th>
                        <th className="py-2 pr-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {utilityTransactions.map((tx, index) => (
                        <tr key={getUtilitySettlementId(tx) || tx._id || index} className="border-b border-gray-100 last:border-b-0">
                          <td className="whitespace-nowrap py-2 pr-3 text-gray-600">
                            {formatDateWithTime(tx.timestamp || tx.createdAt)}
                          </td>
                          <td className="py-2 pr-3 text-gray-800">
                            {tx.description || 'Utility income'}
                          </td>
                          <td className="whitespace-nowrap py-2 pr-3 text-right font-semibold text-blue-800">
                            {formatMoney(tx.amount || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {myProperties.length > 0 ? (
          myProperties.map((propertyData, index) => {
            const { property, units } = propertyData;
            const occupiedUnits = units.filter(unit => unit.tenant?._id);
            const vacantUnits = units.filter(unit => !unit.tenant?._id);
            const isLocked = !!(property?.locked || property?.managementCompany?.locked);
            const occPctL = units.length > 0 ? Math.round((occupiedUnits.length / units.length) * 100) : 0;
            const occToneL =
              occPctL >= 80
                ? "bg-emerald-100 text-emerald-700"
                : occPctL >= 50
                  ? "bg-amber-100 text-amber-700"
                  : "bg-rose-100 text-rose-700";

            return (
              <div
                key={property._id}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
                onClick={() => {
                  if (isLocked) return;
                  handlePropertyClick(propertyData);
                }}
              >
                <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 to-green-500" />
                {isLocked && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/80 rounded-xl">
                    <div className="relative flex items-center justify-center w-full h-full">
                      <div className="absolute inset-4 border-4 border-red-600 rounded-xl" />
                      <svg
                        className="w-20 h-20 text-red-600 opacity-80"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow">
                        Blocked
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex flex-1 flex-col gap-3 p-4">
                  {/* Header: icon + name + occupancy % */}
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                      <Icon name="house-door" size="1.2rem" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h5 className="truncate text-base font-bold leading-tight text-gray-900">{property.name}</h5>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                        <Icon name="geo-alt" size="0.7rem" className="shrink-0" />
                        <span className="truncate">{property.address}</span>
                      </p>
                    </div>
                    <span className={`shrink-0 rounded-lg px-2 py-1 text-[11px] font-bold ${occToneL}`}>{occPctL}%</span>
                  </div>

                  {/* Occupancy progress */}
                  <div>
                    <div className="flex items-center justify-between text-[11px] font-medium text-gray-500">
                      <span>Occupied {occupiedUnits.length}/{units.length}</span>
                      <span>Vacant {vacantUnits.length}</span>
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-green-500"
                        style={{ width: `${occPctL}%` }}
                      />
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="space-y-1 text-xs text-gray-500">
                    <p className="flex items-center gap-1.5">
                      <Icon name="building" size="0.78rem" className="shrink-0 text-gray-400" />
                      <span className="truncate">Mgmt: {property?.managementCompany?.companyName || 'N/A'}</span>
                    </p>
                    <p className="flex items-center gap-1.5">
                      <Icon name="telephone" size="0.78rem" className="shrink-0 text-gray-400" />
                      <span className="truncate">Tel: {property?.manager?.phone || 'N/A'}</span>
                    </p>
                    <p className="flex items-center gap-1.5">
                      <Icon name="percent" size="0.78rem" className="shrink-0 text-gray-400" />
                      <span className="truncate">
                        Mgmt Rate:{' '}
                        {typeof property?.managementPercentage === 'number' ? `${property.managementPercentage}%` : 'N/A'}
                      </span>
                    </p>
                    <p className="flex items-center gap-1.5">
                      <Icon name="cash-coin" size="0.78rem" className="shrink-0 text-gray-400" />
                      <span className="truncate">
                        Settlement: {property?.redirectLandlordIncomeToManager ? 'Mgmt Externally' : 'RecordLOGS'}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="mt-auto border-t border-gray-100 p-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isLocked) return;
                      handlePropertyClick(propertyData);
                    }}
                    disabled={isLocked}
                    className={`flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-white transition-colors ${isLocked ? 'cursor-not-allowed bg-gray-300' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                  >
                    {isLocked ? (
                      <>
                        <Icon name="lock-fill" size="0.85rem" />
                        Blocked
                      </>
                    ) : (
                      <>
                        <Icon name="grid-3x3-gap" size="0.85rem" />
                        View Units
                        <Icon name="arrow-right" size="0.85rem" className="transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <h4 className="text-xl font-semibold text-gray-700 mb-2">No Properties Yet</h4>
            <p className="text-gray-500">No properties assigned to you yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LandlordView;
